import React, { useEffect, } from "react";
import {
  Modal,
  Input,
  Select,
  DatePicker,
  Button,
  Row,
  Col,
  Divider,
  Typography,
  Form,
  Space,
} from "antd";
import dayjs from "dayjs";
import { targetsController } from "./controller";
import { isNotEmpty } from "../../utils";

const { Title, Text } = Typography;
const { TextArea } = Input;

const ModalEditTarget = ({
  open,
  onCancel,
  onSuccess,
  target,
  data,
}) => {
  const [form] = Form.useForm();
  const {
    metricsList = [],
    rewardTypes = [],
    periods = [],
    officeUsers = [],
  } = data || {};

  useEffect(() => {
    if (target) {
      form.setFieldsValue({
        name: target.rule_name,
        description: target.description,
        reward_type: target.reward_type,
        reward_amount: target.reward_amount,
        period: target.period,
        date_range: target.start_date && target.end_date
          ? [dayjs(target.start_date), dayjs(target.end_date)]
          : undefined,        
        branch: localStorage.getItem("selectedOffice"),
        user_ids: target.user_ids.map((u) => Number(u[0])),  // 👈 extract id only,
        actions: target.actions.map((a) => ({
          metric_id: a.metric_id,
          target: Number(a.target),
        })),
      });
    }
  }, [target]);

  const handleUpdate = async () => {
    const values = await form.validateFields();
    const payload = {
      ...values,
      start_date: values.date_range[0].toISOString(),
      end_date: values.date_range[1].toISOString(),
      actions: values.actions.map((a) => ({
        id: a.metric_id,      // 👈 metric_id -> id
        target: Number(a.target),
      })),
    };
    delete payload.date_range;

    await targetsController.updateTarget(target.id, payload);

    onSuccess();
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={640}
      centered
    >
      <Title level={4}>Edit Your Target</Title>
      <Text type="secondary">
        Set a performance target and reward for your team
      </Text>

      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: 24 }}
        initialValues={{
          actions: [{}],
        }}
      >
        {/* Name */}
        <Form.Item
          label="Target Name"
          name="name"
          rules={[{ required: true }]}
        >
          <Input size="large" />
        </Form.Item>

        {/* Dynamic Metric Section */}
        <Form.List name="actions">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => (
                <Row gutter={16} key={field.key}>
                  <Col span={11}>
                    <Form.Item
                      {...field}
                      label={index === 0 ? "Metric" : ""}
                      name={[field.name, "metric_id"]}
                      rules={[{ required: true }]}
                    >
                      <Select
                        size="large"
                        placeholder="Select metric"
                        options={metricsList.map((m) => ({
                          value: m.id,
                          label: m.name,
                        }))}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={11}>
                    <Form.Item
                      {...field}
                      label={index === 0 ? "Target Value" : ""}
                      name={[field.name, "target"]}
                      rules={[{ required: true }]}
                    >
                      <Input size="large" />
                    </Form.Item>
                  </Col>

                  <Col span={2} style={{ display: "flex", alignItems: "center" }}>
                    {fields.length > 1 && (
                      <Button
                        danger
                        onClick={() => remove(field.name)}
                      >
                        X
                      </Button>
                    )}
                  </Col>
                </Row>
              ))}

              <Button
                type="dashed"
                onClick={() => add()}
                style={{ marginBottom: 16 }}
              >
                + Add Another
              </Button>
            </>
          )}
        </Form.List>

        <Row gutter={16} style={{ marginTop: 8 }}>
          <Col span={8}>
            <Form.Item
              label="Period"
              name="period"
              rules={[{ required: true }]}
            >
              <Select
                size="large"
                options={isNotEmpty(periods) && periods.map((p) => ({
                  value: p.key,
                  label: p.label,
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={16}>
            <Form.Item
              label="Start & End Date"
              name="date_range"
              rules={[{ required: true }]}
            >
              <DatePicker.RangePicker
                size="large"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>


        {/* Assign To */}
        <Form.Item
          label="Assign To"
          name="user_ids"
          rules={[{ required: true }]}
        >
          <Select
            mode="multiple"
            size="large"
            placeholder="Select users"
            options={isNotEmpty(officeUsers) && officeUsers.map((u) => ({
              value: Number(u.id),
              label: u.username,
            }))}
          />
        </Form.Item>

        <Divider />

        <Title level={5}>Reward</Title>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Reward Type"
              name="reward_type"
              rules={[{ required: true }]}
            >
              <Select
                size="large"
                options={isNotEmpty(rewardTypes) && rewardTypes.map((r) => ({
                  value: r.key,
                  label: r.label,
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Reward Amount"
              name="reward_amount"
              rules={[{ required: true }]}
            >
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>

        {/* Description */}
        <Form.Item
          label="Description"
          name="description"
        >
          <TextArea rows={3} />
        </Form.Item>

        {/* Buttons */}
        <Space
          style={{
            width: "100%",
            justifyContent: "flex-end",
            marginTop: 16,
          }}
        >
          <Button onClick={onCancel}>
            Cancel
          </Button>

          <Button
            type="primary"
            onClick={handleUpdate}
            style={{ backgroundColor: "#9810fa" }}
          >
            Update Target
          </Button>
        </Space>
      </Form>
    </Modal>
  );
};

export default ModalEditTarget;
